import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import styled from 'styled-components';
import {Button, Detail, EMPHASIS, Heading, Icon} from '@forecast-it/design-system';
import Util from '../../../forecast-app/shared/util/util';
import UpdateFeatureFlag from '../../../mutations/mission_control/feature_flag_mutation';
import {FeatureFlagDetailData} from './featureFlagDetailData';
import {FeatureFlagAssignmentTable} from './FeatureFlagAssignmentTable';
import {FeatureFlagBulkRemoveBar} from './FeatureFlagBulkRemoveBar';
import {FeatureFlagRemoveAllModal} from './FeatureFlagRemoveAllModal';
import {FeatureFlagRemoveMenu} from './FeatureFlagRemoveMenu';
import {FeatureFlagRolloutSummary} from './FeatureFlagRolloutSummary';
import {toggleAllCompanies, toggleCompanySelection} from './featureFlagRemoval';
import {buildBulkRemoveInput} from './rollout/featureFlagRollout';
import {
	FEATURE_FLAG_CARD_BORDER,
	FEATURE_FLAG_LINK_TEXT,
	FEATURE_FLAG_NEUTRAL_FILL,
	featureFlagCardSurface,
} from './featureFlagSurface';
import {RolloutCohortSource} from './rollout/featureFlagRolloutCohort';
import {RolloutDirectory} from './rollout/featureFlagRolloutDirectory';
import {FeatureFlagRolloutModal} from './rollout/FeatureFlagRolloutModal';

const DetailWrapper = styled.div`
	max-width: 1400px;
	/* Wide enough for the assignment table's columns, so every card on the page keeps one width and a
	   viewport narrower than that is scrolled by the document — the table can no longer absorb it
	   itself, for the reason its own TableCard sets out. */
	min-width: min-content;
	margin: 0 auto;
	padding: 20px 32px 64px;
`;

const BackLink = styled(Link)`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 14px;
	text-decoration: none;

	/* The typography components paint their own colour onto the element they render, so a colour
	   inherited from this link would lose to it and has to be set on the children instead. The arrow
	   takes its colour from its own prop and is unaffected. */
	> * {
		color: ${FEATURE_FLAG_LINK_TEXT};
	}

	&:hover {
		text-decoration: underline;
	}
`;

const HeaderCard = styled.div<{$accent: string}>`
	${featureFlagCardSurface}
	display: flex;
	flex-wrap: wrap;
	align-items: flex-start;
	justify-content: space-between;
	gap: 20px 32px;
	padding: 20px 24px;
	/* Repeats the accent bar on the row this page was opened from, so the two read as one place. */
	border-left: 4px solid ${props => props.$accent};
`;

const HeaderMain = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
	min-width: 0;
`;

const HeaderTitleRow = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 12px;
`;

const TeamPill = styled.span`
	display: flex;
	flex: none;
	align-items: center;
	gap: 7px;
	white-space: nowrap;
	padding: 3px 9px;
	border: 1px solid ${FEATURE_FLAG_CARD_BORDER};
	border-radius: 999px;
	background: ${FEATURE_FLAG_NEUTRAL_FILL};
`;

const TeamSwatch = styled.span<{$colour: string}>`
	width: 9px;
	height: 9px;
	border-radius: 2px;
	flex: none;
	background: ${props => props.$colour};
`;

export interface FeatureFlagDetailProps {
	detail: FeatureFlagDetailData;
	/** Where the flag list lives. Owned by the page, which holds the route. */
	backPath: string;
	/** Company names and per-company people the rollout modal picks from. */
	directory: RolloutDirectory;
	/** The pool the rollout percentage is measured against. */
	eligibleCompanyIds: Set<string>;
	/** The flag-independent cohorts a rollout can target, already sorted by name. */
	cohorts: readonly RolloutCohortSource[];
	/** Encoded ids on the shared always-last list, which a percentage fill reaches only at the end. */
	alwaysLastCompanyIds: ReadonlySet<string>;
}

export function FeatureFlagDetail({
	detail,
	backPath,
	directory,
	eligibleCompanyIds,
	cohorts,
	alwaysLastCompanyIds,
}: FeatureFlagDetailProps): JSX.Element {
	const [isRolloutOpen, setIsRolloutOpen] = useState(false);
	// `null` is a real state distinct from closed: it is the modal open for adding, where no single
	// company is being edited. Hence the separate open flag rather than treating a company id as the
	// whole state.
	const [editingCompanyId, setEditingCompanyId] = useState<string | null>(null);

	// `null` means no bulk removal is in progress. An array — empty included — means the table is in
	// selection mode, so starting a selection and having selected nothing yet are distinguishable
	// without a second flag that could disagree with the list.
	const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[] | null>(null);
	const [isRemoveAllOpen, setIsRemoveAllOpen] = useState(false);
	const [isRemoving, setIsRemoving] = useState(false);

	const openRollout = (companyId: string | null): void => {
		setEditingCompanyId(companyId);
		setIsRolloutOpen(true);
	};

	const allCompanyIds = detail.rows.map(row => row.companyId);

	/**
	 * Send a removal, clearing every removal-related bit of state once it lands.
	 *
	 * The rows themselves are not filtered here: the mutation returns the flag's grants and Relay
	 * repaints the table from them, so removing rows locally as well would be a second source of truth
	 * that disagrees with the server the moment a removal partly fails.
	 */
	const commitRemoval = (companyIds: readonly string[]): void => {
		if (isRemoving || companyIds.length === 0) {
			return;
		}
		setIsRemoving(true);
		Util.CommitMutation(
			UpdateFeatureFlag,
			buildBulkRemoveInput(detail.key, companyIds),
			() => {
				setIsRemoving(false);
				setSelectedCompanyIds(null);
				setIsRemoveAllOpen(false);
			},
			true,
			null,
			(transaction, onFailure) => {
				// onFailure returns true when it has scheduled a transient-network retry instead of showing
				// an error, in which case the removal is still in flight and nothing here should move.
				const retrying = onFailure(transaction);
				if (retrying) {
					return;
				}
				// Without this the page would be stuck: onFailure never calls onSuccess, so `isRemoving`
				// would stay true, and both of the controls that could get the operator out of here are
				// disabled while it is. The bar's Cancel is the only exit from selection mode, so a failed
				// removal would leave a floating bar that cannot be dismissed without reloading.
				setIsRemoving(false);
				// The selection itself is kept. Nothing was removed, so the companies that were picked are
				// still the companies the operator wants gone, and re-ticking a dozen rows after a network
				// blip is a punishment for the server's failure.
				//
				// The confirmation dialog does have to go, and for the reason FeatureFlagRolloutModal
				// documents at length: onFailure has just raised the shared error modal into the legacy
				// layer inside the app root, while this dialog renders in a body portal at the same
				// z-index, and a body portal always comes later in the document. Staying open would paint
				// over the error, so the failure would look like nothing happened and the correlation id
				// needed to report it would never be seen.
				setIsRemoveAllOpen(false);
			}
		);
	};

	return (
		<DetailWrapper>
			<BackLink data-cy="feature-flag-detail-back" to={backPath}>
				<Icon size="s" icon="arrowLeft" newColor="primary" />
				<Detail>All feature flags</Detail>
			</BackLink>
			<HeaderCard $accent={detail.teamColour}>
				<HeaderMain>
					<HeaderTitleRow>
						<Heading size="3">{detail.name}</Heading>
						<TeamPill>
							{/* Decorative: it restates the team named beside it, so an accessible name here
							    would make the pill announce its colour before its team. */}
							<TeamSwatch
								aria-hidden="true"
								data-testid="feature-flag-detail-team-swatch"
								$colour={detail.teamColour}
							/>
							<Detail>{detail.teamLabel}</Detail>
						</TeamPill>
					</HeaderTitleRow>
					<Detail color="medium">Flag name and owning team are defined in backend code — not editable here.</Detail>
				</HeaderMain>
				<Button emphasis={EMPHASIS.HIGH} icon="plus" onPress={() => openRollout(null)}>
					Roll out to more companies
				</Button>
			</HeaderCard>
			<FeatureFlagRolloutSummary detail={detail} />
			<FeatureFlagAssignmentTable
				headerAction={
					// Nothing to remove from an empty table, and a menu offering to clear no companies would
					// only be a control that cannot do anything.
					detail.rows.length > 0 ? (
						<FeatureFlagRemoveMenu
							onBulkRemove={() => setSelectedCompanyIds([])}
							onRemoveAll={() => setIsRemoveAllOpen(true)}
						/>
					) : null
				}
				onEditCompany={companyId => openRollout(companyId)}
				rows={detail.rows}
				selection={
					selectedCompanyIds == null
						? undefined
						: {
								selectedCompanyIds,
								onToggleCompany: companyId =>
									setSelectedCompanyIds(selected => toggleCompanySelection(selected ?? [], companyId)),
								onToggleAll: () =>
									setSelectedCompanyIds(selected => toggleAllCompanies(selected ?? [], allCompanyIds)),
						  }
				}
			/>
			{selectedCompanyIds != null ? (
				<FeatureFlagBulkRemoveBar
					isRemoving={isRemoving}
					onCancel={() => setSelectedCompanyIds(null)}
					onRemove={() => commitRemoval(selectedCompanyIds)}
					selectedCount={selectedCompanyIds.length}
				/>
			) : null}
			{isRemoveAllOpen ? (
				<FeatureFlagRemoveAllModal
					// Counted off the rows rather than `companyCount`, which counts only all-users grants: a
					// company listed because of its named users is cleared by this too. The table's own header
					// counts the same way, so the two numbers on screen agree.
					assignedCount={detail.rows.length}
					flagName={detail.name}
					isRemoving={isRemoving}
					onClose={() => setIsRemoveAllOpen(false)}
					onConfirm={() => commitRemoval(allCompanyIds)}
				/>
			) : null}
			{isRolloutOpen ? (
				<FeatureFlagRolloutModal
					alwaysLastCompanyIds={alwaysLastCompanyIds}
					cohorts={cohorts}
					detail={detail}
					directory={directory}
					editingCompanyId={editingCompanyId}
					eligibleCompanyIds={eligibleCompanyIds}
					onClose={() => setIsRolloutOpen(false)}
				/>
			) : null}
		</DetailWrapper>
	);
}
